import type { FC, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { DashboardWidgetType } from 'src/constants/dashboard';
import type { UpstreamApiDashboardData } from 'src/types/dashboard';
import { dashboardContainsWidgetTypes } from 'src/components/dashboard/DashboardContext/helper';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext/index';
import useDashboardFilters from 'src/components/dashboard/hooks/useDashboardFilters';
import useStatisticsData from 'src/components/dashboard/hooks/widgetData/useStatisticsData';

export interface StatisticsValue {
  data: UpstreamApiDashboardData | undefined;
  errorMsg: string;
  loading: boolean;
  refetch: () => void;
  noData: boolean;
}

export const defaultStatisticsValue: StatisticsValue = {
  data: undefined,
  errorMsg: '',
  loading: false,
  refetch: () => null,
  noData: false,
};

export const StatisticsContext = createContext<StatisticsValue>(
  defaultStatisticsValue,
);

const statisticsWidgets = [
  DashboardWidgetType.RESPONSE_MANAGER,
  DashboardWidgetType.DOUGHNUT,
  DashboardWidgetType.RESPONSE_RATE_LEGACY,
  DashboardWidgetType.STACKED_BAR,
  DashboardWidgetType.TABLE,
  DashboardWidgetType.BUBBLE,
  DashboardWidgetType.SCATTER,
  DashboardWidgetType.BAR_TABLE,
  DashboardWidgetType.TREND_ANALYSIS,
  DashboardWidgetType.SPEEDOMETER_RE,
  DashboardWidgetType.GENERIC_TREND_ANALYSIS,
  DashboardWidgetType.BAR_DIAGRAM,
  DashboardWidgetType.FLEXIBLE_CHART,
];

export const StatisticsContextProvider: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const { dashboardConfig } = useDashboardContext();

  const isEnabled = useMemo(
    () => dashboardContainsWidgetTypes(dashboardConfig, statisticsWidgets),
    [dashboardConfig],
  );

  const { flatFiltersDebounced } = useDashboardFilters();
  const statisticsData = useStatisticsData(
    dashboardConfig?.id,
    flatFiltersDebounced,
    isEnabled,
  );

  return (
    <StatisticsContext.Provider value={statisticsData}>
      {children}
    </StatisticsContext.Provider>
  );
};

export const useStatistics = () => useContext(StatisticsContext);

export default StatisticsContextProvider;
